// ABOUTME: SPEC-46 Rev 7 — the checkout panel's state: offered, open, paying, complete, failed.
// ABOUTME: One useReducer composed into App.js as a single hook call (useWaitlist's shape).
//
// Rev 6 opened Stripe in a second browser tab, which is why both of VER-86's bugs existed: the
// payment finished in a tab that could not talk to the one holding the WebSocket, and the
// confirmation arrived in a third request context that could not reach a socket at all. Rev 7
// keeps the guest here. There is no `window.open` in this file, and there must never be one
// again — the tab is the bug.
//
// Two ways in, unchanged (useWaitlist's shape):
//   - `requestCheckout()` — the "Continue to Checkout" button in IconRail/MobileChrome, sending
//     `checkout_request` over the WS.
//   - `handleServerMessage(data)` — an `offer_checkout` ui_update, from that button OR from the
//     agent's own `proceed_to_checkout` tool. App.js routes it in beside `waitlist_form`.
//
// VER-86 A.5 — an offer OPENS the panel, however it was asked for. Rev 7 first kept D-8's "a
// tap is the gesture" rule and made a voice-initiated offer merely arm the CTA, so the agent had
// to tell the guest to click. That rule was a browser constraint, not a safety rule: it existed
// because `window.open` needs a real user gesture and a WebSocket message has none. With an
// in-page overlay there is no gesture to satisfy, and asking someone to click a button after
// they have just said "yes, check out" is friction invented by a workaround that is gone.
//
// What protects the guest is unchanged and is on the backend: D-7's two-phase gate means an
// offer is only ever dispatched after a bag button tap or an explicit spoken yes. There is no
// path that puts a payment panel in front of someone who did not ask for one.

import { useCallback, useReducer, useRef } from 'react';

const initialState = {
  offer: null,   // { clientSecret, providerSessionId, dueTodayDisplay, lineCount } | null
  open: false,   // is the payment panel mounted
  status: 'idle', // idle | requesting | open | complete | error
  error: '',     // '' | loader_failed | mount_failed | expired | offline
};

function reducer(state, action) {
  switch (action.type) {
    case 'REQUEST':
      return { ...state, status: 'requesting' };
    case 'OFFER':
      return { ...state, offer: action.offer, open: action.autoOpen, status: action.autoOpen ? 'open' : 'idle' };
    case 'ERROR':
      // `open: true` matters. Every error message this feature has lives in the panel, so an
      // error that leaves the panel closed is a silent failure: the guest taps Checkout on a
      // load or mount failure and simply nothing happens (external review, 2026-09-15). The
      // panel is closable in every state, so opening it is always safe. A missing publishable
      // key is NOT one of these errors any more (Rev 7, JC AC-30) — that case never dispatches.
      return { ...state, status: 'error', error: action.reason, open: true };
    case 'COMPLETE':
      return { ...state, status: 'complete' };
    case 'CLOSE':
      return { ...initialState };
    default:
      return state;
  }
}

/** @param {{ sendMessage?: (msg: object) => void, publishableKey?: string }} config */
export function useCheckout({ sendMessage, publishableKey = '' } = {}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  // Not reducer state: this only decides whether the NEXT offer auto-opens, and flipping it must
  // never cause a render.
  const tappedRef = useRef(false);

  const requestCheckout = useCallback(() => {
    // A deployment with no publishable key has no checkout. Rev 7 (JC AC-30, D-23): the gap
    // must be INVISIBLE to the guest — the CTA still renders (spec's byte-identity rule E4),
    // it just does nothing when tapped, rather than sending a request or surfacing an error.
    if (!publishableKey) return;
    if (tappedRef.current) return;  // already asking — a second tap is not a second checkout
    tappedRef.current = true;
    dispatch({ type: 'REQUEST' });
    // App's sender returns false when the socket is not open. Ignoring that left the hook in
    // `requesting` forever with the in-flight guard still set, so every later tap was swallowed
    // too — a dead socket made the Checkout button permanently inert with no message.
    const sent = sendMessage?.({ type: 'checkout_request' });
    if (sent === false) {
      tappedRef.current = false;
      dispatch({ type: 'ERROR', reason: 'offline' });
    }
  }, [sendMessage, publishableKey]);

  const handleServerMessage = useCallback((data) => {
    // A deployment with no publishable key has no checkout (Rev 7, JC AC-30): mounting an
    // offer here would drive CheckoutOverlay's Stripe effect with a key it does not have.
    if (!publishableKey) return;
    const offer = {
      clientSecret: data?.presentation_token || '',
      providerSessionId: data?.provider_session_id || '',
      dueTodayDisplay: data?.due_today_display || '',
      lineCount: typeof data?.line_count === 'number' ? data.line_count : 0,
    };
    // Every offer opens: the backend only dispatches one after a tap or an explicit spoken yes.
    const autoOpen = Boolean(offer.clientSecret);
    tappedRef.current = false;
    if (!offer.clientSecret) {
      // The backend raises rather than offering a session it could not create, so this is the
      // belt to that braces: never mount a panel with nothing to mount.
      dispatch({ type: 'ERROR', reason: 'mount_failed' });
      return;
    }
    dispatch({ type: 'OFFER', offer, autoOpen });
  }, [publishableKey]);

  const fail = useCallback((reason) => {
    dispatch({ type: 'ERROR', reason });
  }, []);

  /**
   * Stripe says the payment is done. The client is a HINT, never the authority: the backend
   * confirms it against its own marker or by asking Stripe, and only then decrements the bag and
   * speaks. All this does is tell the session where to look.
   */
  const complete = useCallback(() => {
    dispatch({ type: 'COMPLETE' });
    sendMessage?.({
      type: 'checkout_complete',
      provider_session_id: state.offer?.providerSessionId || '',
    });
  }, [sendMessage, state.offer]);

  const close = useCallback(() => {
    tappedRef.current = false;
    dispatch({ type: 'CLOSE' });
  }, []);

  return {
    offer: state.offer,
    open: state.open,
    status: state.status,
    error: state.error,
    publishableKey,
    requestCheckout,
    handleServerMessage,
    complete,
    fail,
    close,
  };
}
